import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoginWithEmail from '../../components/Login/LoginWithEmail';
import { isafeDirectSSOLogin } from '../../sagas/actions';
import { actions } from '../../util/actions';

const LandingPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 80vh;
`;

const LoginBoxContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 20px;
`;

const LoginBox = styled.div`
  background: rgba(255, 255, 255, 0.8);
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  width: 400px;
  max-width: 90%;
  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const Title = styled.h2`
  font-size: 1.8rem;
  color: #FFFFFF;
  margin-bottom: 20px;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
`;

const CancelLink = styled.a`
  color: #195174;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const SubmitButton = styled.input`
  padding: 10px 20px;
  font-size: 1rem;
  color: white;
  background-color: #195174;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background-color: #2980b9;
  }
`;

const InfoText = styled.p`
  font-size: 0.9rem;
  color: #555;
  margin-top: 20px;
  text-align: center;

  a {
    color: #195174;
    text-decoration: underline;
    &:hover {
      color: #2980b9;
    }
  }
`;

const SsoOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 20px;
`;

const SsoBanner = styled.div`
  background: rgba(255, 255, 255, 0.96);
  border-radius: 12px;
  padding: 28px 32px;
  width: min(520px, 92%);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
  text-align: center;
  color: #195174;
`;

const SsoBannerTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 1.35rem;
  font-weight: 700;
`;

const SsoBannerMessage = styled.p`
  margin: 0 0 16px;
  font-size: 0.95rem;
  color: #2d5877;
`;

const SsoSpinner = styled.span`
  display: inline-block;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid rgba(25, 81, 116, 0.2);
  border-top-color: #195174;
  animation: spin 0.9s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const SsoErrorBanner = styled.div`
  background: rgba(139, 30, 47, 0.08);
  border: 1px solid rgba(139, 30, 47, 0.35);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 18px;
  text-align: left;
  color: #8b1e2f;
`;

const SsoErrorTitle = styled.div`
  font-weight: 700;
  margin-bottom: 6px;
`;

const SsoErrorText = styled.div`
  font-size: 0.9rem;
  color: #8b1e2f;
`;

const SsoErrorDismiss = styled.button`
  margin-top: 10px;
  background: transparent;
  border: 1px solid rgba(139, 30, 47, 0.5);
  color: #8b1e2f;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.85rem;
  cursor: pointer;

  &:hover {
    background: rgba(139, 30, 47, 0.08);
  }
`;

const ISAFE_SSO_TOKEN_QUERY_KEYS = ['access_token', 'access-token', 'token'];
const ISAFE_SSO_QUERY_KEYS_TO_CLEAR = [...ISAFE_SSO_TOKEN_QUERY_KEYS, 'email'];

const getIsafeSsoTokenFromParams = (params) => {
  for (const key of ISAFE_SSO_TOKEN_QUERY_KEYS) {
    const value = params.get(key);
    if (value) {
      return value;
    }
  }
  return '';
};

const clearIsafeSsoParamsFromUrl = (location) => {
  if (typeof window === 'undefined') {
    return;
  }

  const params = new URLSearchParams(location.search);
  let changed = false;
  ISAFE_SSO_QUERY_KEYS_TO_CLEAR.forEach((key) => {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  });

  if (!changed) {
    return;
  }

  const search = params.toString();
  const cleanedUrl = `${location.pathname}${search ? `?${search}` : ''}${location.hash || ''}`;
  window.history.replaceState(window.history.state, '', cleanedUrl);
};

const LandingPage = ({ colors, ...loginProps }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const lastSsoRequestRef = useRef(null);
  const { processing: ssoProcessing, error: ssoError } = useSelector(
    (state) => state.auth?.isafeDirectSso || {}
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accessToken = getIsafeSsoTokenFromParams(params);
    const email = params.get('email');

    if (!accessToken) {
      dispatch({ type: actions.ISAFE_DIRECT_SSO_CLEAR });
      lastSsoRequestRef.current = null;
      return;
    }

    const ssoRequestKey = `${accessToken}|${email || ''}`;
    if (lastSsoRequestRef.current === ssoRequestKey) {
      clearIsafeSsoParamsFromUrl(location);
      return;
    }
    lastSsoRequestRef.current = ssoRequestKey;
    dispatch(isafeDirectSSOLogin({ accessToken, email }));
    clearIsafeSsoParamsFromUrl(location);
  }, [dispatch, location.search]);

  return (
    <LandingPageContainer>
      {(ssoProcessing || ssoError) && (
        <SsoOverlay>
          <SsoBanner role="dialog" aria-live="polite">
            <SsoBannerTitle>ISAFE Secure Access</SsoBannerTitle>
            {ssoError ? (
              <>
                <SsoErrorBanner role="alert">
                  <SsoErrorTitle>ISAFE Single Sign-On Failed</SsoErrorTitle>
                  <SsoErrorText>{ssoError}</SsoErrorText>
                </SsoErrorBanner>
                <SsoErrorDismiss
                  onClick={() =>
                    dispatch({ type: actions.ISAFE_DIRECT_SSO_CLEAR })
                  }
                >
                  Dismiss
                </SsoErrorDismiss>
              </>
            ) : (
              <>
                <SsoBannerMessage>
                  Please wait while we finish signing you in.
                </SsoBannerMessage>
                <SsoSpinner aria-label="Loading" />
              </>
            )}
          </SsoBanner>
        </SsoOverlay>
      )}
      <LoginBoxContainer>
          <Title>Login</Title>
          <LoginWithEmail colors={colors} {...loginProps} />
          <InfoText>
            If you need help remembering your username or password, use the{' '}
            <Link to="/password/request">forgot password tool</Link>.
            {/* or{' '} */}
            {/* <Link to="/register">create an account</Link>. */}
          </InfoText>
          <InfoText>Please remember that usernames and passwords are <strong>case sensitive</strong>.</InfoText>
      </LoginBoxContainer>
    </LandingPageContainer>
  );
};

export default LandingPage;
